import * as Moment from "moment";
import { IManualDateFormatConvertible, ManualDateFormatConverterFactory } from "./manualDateFormat";
import { EraAsJapaneseCalendar } from "./EraAsJapaneseCalendar";
import { LoggerFactory } from "../../utilities/logger";

export class DateFormatter {
  static format(value: Date, format: string = "YYYY/MM/DD", manualFormat: string = null, manualFormatLanguage: string = null): string {
    const filledFormat = !!format ? format : "YYYY/MM/DD";
    const converted = this.applyValue(value, filledFormat, manualFormat, manualFormatLanguage);
    return EscapedFormatProcessor.recoverEscapeRelatedCharacters(converted);
  }

  private static applyValue(value: Date, filledFormat: string, manualFormat: string, manualFormatLanguage: string): string {
    if (!value) {
      return this.toEmptyString(filledFormat, manualFormat, manualFormatLanguage);
    }
    const preFormatted = this.replaceFormat(value, filledFormat, manualFormat, manualFormatLanguage);
    Moment.locale("en");
    return Moment(value).format(preFormatted);
  }

  private static toEmptyString(format: string, manualFormat: string, manualFormatLanguage: string): string {
    return new EscapedFormatProcessor(format)
      // 独自フォーマット指定子の置換.
      .replace(/\$MANUAL\$/g, this.convertManualFormat(null, manualFormat, manualFormatLanguage))
      .replace(/\$JJJ\$/g, "    ")  // 和暦の元号
      .replace(/\$JJ\$/g, "  ")     // 和暦の元号（省略・漢字）
      .replace(/\$J\$/g, "")        // 和暦の元号（省略・英字）
      .replace(/\$YY\$/g, "  ")     // 和暦の年(0埋めあり)
      .replace(/\$Y\$/g, "  ")      // 和暦の年
      .replace(/\$ddd\$/g, "  ")    // 曜日
      .replace(/\$((a)|(A))\$/g, "午前")
      // Momentフォーマット指定子の置換.(必要な物を追加.)
      .replace(/((YYYY)|(YYY)|(YY)|(Y))/g, "    ")
      .replace(/((MM)|(M)|(DD)|(D)|(HH)|(hh)|(mm)|(ss))/g, "  ")
      .replace(/((ddd))/g, "")
      .replace(/((a)|(A))/g, "AM")
      .removeBrackets() // Momentを通さないので、エスケープ用の[]を除去する必要がある
      .format;
  }

  private static replaceFormat(source: Date, format: string, manualFormat: string, manualFormatLanguage: string): string {
    Moment.locale("ja");
    const era = EraAsJapaneseCalendar.from(source);
    const moment = Moment(source);

    // フォーマット指定の対応
    const manualConvertedFormat = new EscapedFormatProcessor(format)
      .replace(/\$MANUAL\$/g, this.convertManualFormat(moment, manualFormat, manualFormatLanguage))
      .format;

    // 和暦記号の指定の場合は元年表示しないで1となる模様
    const enableEraInitialYearConvert = ((era.year === 1) && (manualConvertedFormat.indexOf("$J$") < 0));
    // padStartはIEでは対応していないのでpolyfillを追加しています（追加前でも何かのライブラリで追加しているのか動作はしていた）
    return new EscapedFormatProcessor(manualConvertedFormat)
      .replace(/\$JJJ\$/g, this.toEscapedString(era.name))
      .replace(/\$JJ\$/g, this.toEscapedString(era.shortName))
      .replace(/\$J\$/g, this.toEscapedString(era.initial))
      .replace(/\$YY\$/g, this.toEscapedString((era.year + "").padStart(2, "0")))
      .replace(/\$Y\$/g, this.toEscapedString(enableEraInitialYearConvert ? "元" : era.year))
      .replace(/\$ddd\$/g, this.toEscapedString(moment.format("ddd")))
      .replace(/\$a\$/g, this.toEscapedString(moment.format("a")))
      .replace(/\$A\$/g, this.toEscapedString(moment.format("A")))
      .replace(/hh/g, this.toEscapedString(this.toHarfDayHour(source)))
      .format;
  }

  private static toHarfDayHour(source: Date): string {
    const hour = source.getHours();
    const hourText = ((hour >= 12) ? hour - 12 : hour).toString();
    return (hourText.length === 1) ? "0" + hourText : hourText;
  }

  private static toEscapedString(value: any): string {
    // value自体にフォーマット指定子が含まれてる場合を考慮して[]で囲う.
    return "[" + value.toString() + "]";
  }

  private static convertManualFormat(moment: Moment.Moment, manualFormat: string, manualFormatLanguage: string): string {
    try {
      const converter = new ManualFormatConverter(manualFormat, manualFormatLanguage);
      return converter.convertManualFormat(moment);
    } catch (e) {
      LoggerFactory.getLogger().error("日付の書式の解析に失敗しました。format=" + manualFormat);
      LoggerFactory.getLogger().error(e);
      // Errorはthrowしないようにしているが、予期しないErrorが発生したときにも最低限の日付は出るようにしている
      return "Y 年 M 月 D 日";
    }
  }
}

class ManualFormatConverter {
  constructor(private _manualFormat: string, private _manualFormatLanguage: string) {
  }
  
  public convertManualFormat(moment: Moment.Moment): string {
    if (!this._manualFormat) { return "Y 年 M 月 D 日"; }
    const converter = ManualDateFormatConverterFactory.create(this._manualFormatLanguage);
    return new EscapedFormatProcessor(this._manualFormat)
      .replaceEscapeRelatedCharacters()
      .escapeQuotedText()
      .escapeModifiedCharactor()
      .replace(/ggg/g, converter.getEraName())
      .replace(/gg/g, converter.getShortEraName())
      .replace(/g/g, converter.getEraNameInitial())
      .replaceYear(/y{1,}/g, converter, moment)
      .replace(/MMMM/g, converter.getMonthName(moment))
      .replace(/MMM/g, converter.getShortMonthName(moment))
      .replace(/ddo/g, "DD" + converter.getDateOrdinal(moment))
      .replace(/do/g, "D" + converter.getDateOrdinal(moment))
      .replace(/d{4,}/g, converter.getDayOfWeek(moment))
      .replace(/ddd/g, converter.getShortDayOfWeek(moment))
      .replace(/dd/g, "DD")
      .replace(/d/g, "D")
      .format;
  }
}

class EscapedFormatProcessor {
  constructor(public format: string) {
  }
  
  public replace(searchValue: RegExp, replaceValue: string): EscapedFormatProcessor {
    const splitted = this.splitFormat(this.format);
    const converted = splitted
      .map(s => this.isEscapedFormat(s) ? s : s.replace(searchValue, this.escaseDollar(replaceValue)))
      .join("");
    return new EscapedFormatProcessor(converted);
  }

  /**
   * replaceで$$が$に変換されるため、あらかじめ$を$$に変換する
   * 参考) https://stackoverflow.com/questions/33781510/how-to-replace-in-javascript
   */
  private escaseDollar(value: string): string {
    return !!value
      ? value.replace(/\$/g, "$$$$")
      : value;
  }

  public replaceYear(searchValue: RegExp, converter: IManualDateFormatConvertible, moment: Moment.Moment): EscapedFormatProcessor {
    const splitted = this.splitFormat(this.format);
    const converted = splitted
      .map(s => this.isEscapedFormat(s) ? s : s.replace(searchValue, match => converter.getYear(moment, match.length)))
      .join("");
    return new EscapedFormatProcessor(converted);
  }

  /**
   * []で囲われていない部分と囲われている部分とで文字列を分割する
   * ※[]で囲われていた文字列は[]で囲われたままになっている
   */
  private splitFormat(format: string): string[] {
    if (!format || format.indexOf("[") < 0) { return [format]; }
    const result: string[] = [];
    const splittedByStartSymbol = format.split("[");
    for (let i = 0; i < splittedByStartSymbol.length; ++i) {
      if (i === 0) {
        result.push(splittedByStartSymbol[i]);
        continue;
      }
      const splittedByEndSymbol = ("[" + splittedByStartSymbol[i]).split("]");
      if (splittedByEndSymbol.length === 1) {
        result.push(splittedByEndSymbol[0]);
        continue;
      }
      for (let j = 0; j < splittedByEndSymbol.length; ++j) {
        if (j === splittedByEndSymbol.length - 1) {
          result.push(splittedByEndSymbol[j]);
          continue;
        }
        result.push(splittedByEndSymbol[j] + "]");
      }
    }
    return result;
  }

  /**
   * エスケープ対象の文字は[]で囲われるように事前に分割している
   */
  private isEscapedFormat(format: string): boolean {
    return !!format && format[0] === "[";
  }

  /**
   * エスケープ処理に影響する文字を全角に変換
   */
  public replaceEscapeRelatedCharacters(): EscapedFormatProcessor {
    if (!this.format) { return new EscapedFormatProcessor(""); }

    // 変換処理が終わった後に復帰させるようにしている
    // ・フロントエンド側のmoment.jsで[]をエスケープする方法が見つかっていない
    // ・バックエンド側のDateTime.Format()で'"をエスケープする処理の簡略化のため
    const replaced = this.format
      .replace(/\[/g, "％［％")
      .replace(/\]/g, "％］％")
      .replace(/\\'/g, "％’％")
      .replace(/\\"/g, "％”％");

    return new EscapedFormatProcessor(replaced);
  }

  public static recoverEscapeRelatedCharacters(value: string): string {
    if (!value) { return value; }
    return value
      .replace(/％［％/g, "[")
      .replace(/％］％/g, "]")
      .replace(/％’％/g, "'")
      .replace(/％”％/g, "\"");
  }

  public removeBrackets(): EscapedFormatProcessor {
    if (!this.format) { return new EscapedFormatProcessor(""); }

    const splitted = this.splitFormat(this.format);
    const converted = splitted
      .map(s => this.isEscapedFormat(s) ? s.replace(/^\[/g, "").replace(/\]$/g, "") : s)
      .join("");

    return new EscapedFormatProcessor(converted);
  }

  /**
   * シングルクォーテーションもしくはダブルクォーテーションで囲われた文字を変換対象外にするため、
   * []で囲うように変換している
   */
  public escapeQuotedText(): EscapedFormatProcessor {
    if (!this.format) { return new EscapedFormatProcessor(""); }
    
    const formatTexts: string[] = [];
    const splitted = this.format.split(/['"]/);
    for (let i = 0; i < splitted.length; ++i) {
      if (i % 2 === 0) {
        formatTexts.push(splitted[i]);
      } else {
        formatTexts.push("[" + splitted[i] + "]");
      }
    }
    return new EscapedFormatProcessor(formatTexts.join(""));
  }

  public escapeModifiedCharactor(): EscapedFormatProcessor {
    if (!this.format) { return new EscapedFormatProcessor(""); }

    const splitted = this.splitFormat(this.format);
    const converted = splitted
      .map(s => this.isEscapedFormat(s) ? s : s.replace(/(\\)(.)/g, "[$2]"))
      .join("");

    return new EscapedFormatProcessor(converted);
  }
}
